import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  apiRequest,
  getToken,
  validatePhoneNumber,
  SuccessToast,
  ErrorToast,
  toastConfig,
  devError,
} from "@ridecloud/shared";
import { Toaster } from "react-hot-toast";
import "../css/Profile.css";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";

function Profile() {
  const navigate = useNavigate();
  const [inputFocused, setInputFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    pic: "",
  });
  const [originalData, setOriginalData] = useState({ ...userData });
  const [updatedUserData, setUpdatedUserData] = useState({ ...userData });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await apiRequest(async (APIv1) =>
        APIv1.get("/user/dashboard/user_profile", {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json",
          },
        })
      );

      setUserData(response.data);
      setUpdatedUserData(response.data);
    } catch (error) {
      ErrorToast("Failed to fetch Profile Details");
      devError("Error fetching Profile Details:", error);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserData((prev) => ({
          ...prev,
          pic: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "phone" ? validatePhoneNumber(value) : value;

    setUpdatedUserData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleEditClick = () => {
    setOriginalData({ ...userData });
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setUserData({ ...originalData });
    setUpdatedUserData({ ...originalData });
    setIsEditing(false);
  };

  const saveProfile = async (updatedUserData) => {
    try {
      const response = await apiRequest(async (APIv1) =>
        APIv1.put("/user/dashboard/update_profile", updatedUserData, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json",
          },
        })
      );
      SuccessToast("Profile updated successfully");
    } catch (error) {
      setUserData({ ...originalData });
      setIsEditing(false);
      ErrorToast("Failed to update Profile Details");
      devError("Error updating Profile Details:", error);
    }
  };

  const handleSaveClick = () => {
    setIsEditing(false);
    setUserData(updatedUserData);
    saveProfile(updatedUserData);
  };

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div
          className="profile-back-icon-container"
          onClick={() => navigate("/dashboard")}
        >
          <ArrowLeft className="profile-back-icon" />
        </div>
        <Toaster {...toastConfig} />
        <div className="profile-header">
          <img src={userData.pic} alt="Profile" className="profile-avatar" />
          <input
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            style={{ display: "none" }}
            id="avatar-upload"
          />
          <label htmlFor="avatar-upload" className="change-picture-button">
            <CameraAltRoundedIcon /> Change Picture
          </label>
          <h1 className="profile-name">
            {userData.first_name} {userData.last_name}
          </h1>
          <p className="profile-email">{userData.email}</p>
        </div>

        <div className="profile-info">
          <div className="full-name">
            <div className="info-section info-section-fullname">
              <div className="info-icon-person">
                <PersonRoundedIcon />
              </div>
              <div className="info-content">
                <h3>First name</h3>
                {isEditing ? (
                  <input
                    type="text"
                    name="first_name"
                    value={updatedUserData.first_name}
                    onChange={handleInputChange}
                    className="edit-input"
                    placeholder="Enter your first name"
                  />
                ) : (
                  <p>{userData.first_name}</p>
                )}
              </div>
            </div>
            <div className="info-section info-section-fullname">
              <div className="info-content">
                <h3>Last name</h3>
                {isEditing ? (
                  <input
                    type="text"
                    name="last_name"
                    value={updatedUserData.last_name}
                    onChange={handleInputChange}
                    className="edit-input"
                    placeholder="Enter your last name"
                  />
                ) : (
                  <p>{userData.last_name}</p>
                )}
              </div>
            </div>
          </div>

          <div className="info-section">
            <div className="info-icon-phone">
              <LocalPhoneRoundedIcon />
            </div>
            <div className="profile-phone-input">
              <h3>Phone Number</h3>

              {isEditing ? (
                <div className="phone-input-container">
                  <input
                    type="text"
                    className={`profile-country-code ${
                      inputFocused ? "focused" : ""
                    }`}
                    value="+91"
                    readOnly
                  />
                  <input
                    type="tel"
                    name="phone"
                    className="phone-number"
                    value={updatedUserData.phone}
                    onChange={handleInputChange}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    placeholder="Enter your phone number"
                  />
                </div>
              ) : (
                <p> +91 {userData.phone}</p>
              )}
            </div>
          </div>

          <div className="info-section">
            <div className="info-icon-email">
              <MailRoundedIcon />
            </div>
            <div className="info-content">
              <h3>Email Address</h3>
              <p>{userData.email}</p>
            </div>
          </div>

          {isEditing ? (
            <div className="button-group">
              <button className="save-button" onClick={handleSaveClick}>
                Save Changes
              </button>
              <button className="cancel-button" onClick={handleCancelClick}>
                Cancel
              </button>
            </div>
          ) : (
            <button className="edit-button" onClick={handleEditClick}>
              Edit Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
